import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Typography,
  Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { birdWeightsService } from '../../services/apiServices';

interface BirdWeightDialogProps {
  open: boolean;
  onClose: () => void;
  birdId?: number;
  birds?: Array<{ id: number; name: string }>;
}

const BirdWeightDialog: React.FC<BirdWeightDialogProps> = ({
  open,
  onClose,
  birdId,
  birds = [],
}) => {
  const queryClient = useQueryClient();
  const [selectedBirdId, setSelectedBirdId] = useState<string>(birdId?.toString() || '');
  const [averageWeight, setAverageWeight] = useState<string>('');
  const [sampleSize, setSampleSize] = useState<string>('10');
  const [weighDate, setWeighDate] = useState<Dayjs | null>(dayjs());
  const [notes, setNotes] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  const recordWeightMutation = useMutation({
    mutationFn: birdWeightsService.create,
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['birdWeights'] });
      queryClient.invalidateQueries({ queryKey: ['birdWeight', selectedBirdId] });

      // Show success message with weight status
      if (data.weight_status) {
        const status = data.weight_status.status;
        let statusMessage = '';

        if (status === 'severely_underweight') {
          statusMessage = '⚠️ WARNING: Bird is severely underweight!';
        } else if (status === 'underweight') {
          statusMessage = '⚠️ Bird is underweight - review feeding program';
        } else if (status === 'standard') {
          statusMessage = '✅ Bird weight is within normal range';
        } else if (status === 'slightly_overweight' || status === 'overweight') {
          statusMessage = '⚠️ Bird is overweight';
        }

        setSuccessMessage(statusMessage);
        setTimeout(() => handleClose(), 2500);
      } else {
        handleClose();
      }
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || 'Failed to record bird weight');
    },
  });

  const handleClose = () => {
    setSelectedBirdId(birdId?.toString() || '');
    setAverageWeight('');
    setSampleSize('10');
    setWeighDate(dayjs());
    setNotes('');
    setError('');
    setSuccessMessage('');
    onClose();
  };

  const handleSubmit = () => {
    setError('');

    // Validation
    if (!selectedBirdId) {
      setError('Please select a bird');
      return;
    }

    const weight = parseFloat(averageWeight);
    if (isNaN(weight) || weight <= 0) {
      setError('Please enter a valid weight (greater than 0)');
      return;
    }

    const size = parseInt(sampleSize);
    if (isNaN(size) || size <= 0) {
      setError('Please enter a valid sample size (greater than 0)');
      return;
    }

    if (!weighDate) {
      setError('Please select a weigh date');
      return;
    }

    recordWeightMutation.mutate({
      bird_id: parseInt(selectedBirdId),
      average_weight_kg: weight,
      sample_size: size,
      weigh_date: weighDate.format('YYYY-MM-DD'),
      notes: notes || undefined,
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Record Bird Weight</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert
              severity={successMessage.includes('✅') ? 'success' : 'warning'}
              onClose={() => setSuccessMessage('')}
            >
              {successMessage}
            </Alert>
          )}

          {!birdId && birds.length > 0 && (
            <TextField
              label="Select Bird"
              select
              value={selectedBirdId}
              onChange={(e) => setSelectedBirdId(e.target.value)}
              fullWidth
              required
            >
              <MenuItem value="">Select a bird</MenuItem>
              {birds.map((bird) => (
                <MenuItem key={bird.id} value={bird.id}>
                  {bird.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {birdId && (
            <Typography variant="body2" color="text.secondary">
              Recording weight for Bird #{birdId}
            </Typography>
          )}

          <TextField
            label="Average Weight (kg)"
            type="number"
            value={averageWeight}
            onChange={(e) => setAverageWeight(e.target.value)}
            fullWidth
            required
            inputProps={{ step: '0.001', min: '0', max: '10' }}
            helperText="Enter average weight in kilograms"
          />

          <TextField
            label="Sample Size"
            type="number"
            value={sampleSize}
            onChange={(e) => setSampleSize(e.target.value)}
            fullWidth
            required
            inputProps={{ step: '1', min: '1', max: '1000' }}
            helperText="Number of birds weighed"
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Weigh Date"
              value={weighDate}
              onChange={(newValue) => setWeighDate(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                },
              }}
            />
          </LocalizationProvider>

          <TextField
            label="Notes"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            placeholder="Optional notes about the weighing session"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={recordWeightMutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={recordWeightMutation.isPending}
        >
          {recordWeightMutation.isPending ? 'Recording...' : 'Record Weight'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BirdWeightDialog;
